import { cleanNumber, formatAWSDate } from '../../../helpers/functions';
import {
  CreateStrategyAssetInput,
  FormValues,
  StopProfitAndLoss,
  UpdateStrategyAssetInput,
} from '../types';
import { FormikProps } from 'formik';
import { ConnectionTypeEnum, CurrencyCodeEnum } from '../../../constants/enums';

function prepareManualInput(
  action: 'CREATE' | 'EDIT',
  profileId: string,
  connectionType: ConnectionTypeEnum,
  formik: FormikProps<FormValues>,
  reportingCurrency: CurrencyCodeEnum,
  strategyAssetCategoryId?: string,
  assetId?: string
): CreateStrategyAssetInput[] | UpdateStrategyAssetInput[] {
  const { inspirationSource, priceAlert, strategyId, quantity } = formik.values;

  const purchasedAt = formatAWSDate(
    formik.values.purchasedAt ? new Date(formik.values.purchasedAt) : new Date()
  );

  // Prepare strategyAssetTargets
  const strategyAssetTargets = formik.values.stopProfitAndLossEntries.reduce(
    (acc, t) =>
      t.endDate && t.stopLoss && t.stopProfit
        ? acc.concat({
            alert: priceAlert,
            endDate: formatAWSDate(new Date(t.endDate)),
            stopProfit: t.stopProfit,
            stopProfitPercentage: t.stopProfitPercentage,
            stopProfitInAssetCurrency: t.stopProfitInAssetCurrency,
            stopProfitInReportingCurrency: t.stopProfitInReportingCurrency,
            stopLoss: t.stopLoss,
            stopLossPercentage: t.stopLossPercentage,
            stopLossInReportingCurrency: t.stopLossInReportingCurrency,
            stopLossInAssetCurrency: t.stopLossInAssetCurrency,
          })
        : acc,
    [] as StopProfitAndLoss[]
  );

  const commonVars = {
    alert: formik.values?.priceAlert,
    bank: formik.values.bank,
    ...(formik.values.currentPriceInReportingCurrency && {
      currentPrice: formik.values.currentPriceInReportingCurrency,
      currentPriceInReportingCurrency:
        formik.values.currentPriceInReportingCurrency,
      currentPriceInAssetCurrency: formik.values.currentPriceInAssetCurrency,
    }),
    entryPrice: formik.values.entryPriceInReportingCurrency ?? 0,
    entryPriceInReportingCurrency: formik.values.entryPriceInReportingCurrency,
    entryPriceInAssetCurrency: formik.values.entryPriceInAssetCurrency,
    entryExchangePrice: formik.values.entryExchangePrice,
    inspirationSource,
    name: formik.values.assetName,
    profileId,
    purchasedAt,
    strategyAssetTargets,
    strategyId,
    unit: cleanNumber(quantity),
  };

  if (action === 'EDIT') {
    const result: UpdateStrategyAssetInput[] = [
      {
        id: formik.values.strategyAssetId!,
        ...commonVars,
      },
    ];
    return result;
  } else {
    const result: CreateStrategyAssetInput[] = [
      {
        ...(assetId && { assetId }),
        connectionType,
        currency: reportingCurrency,
        ...commonVars,
        strategyAssetCategoryId,
      },
    ];
    return result;
  }
}

export function prepareManualCreateInput(
  profileId: string,
  connectionType: ConnectionTypeEnum,
  formik: FormikProps<FormValues>,
  reportingCurrency: CurrencyCodeEnum,
  strategyAssetCategoryId?: string,
  assetId?: string
): CreateStrategyAssetInput[] {
  return prepareManualInput(
    'CREATE',
    profileId,
    connectionType,
    formik,
    reportingCurrency,
    strategyAssetCategoryId,
    assetId
  ) as CreateStrategyAssetInput[];
}

export function prepareManualEditInput(
  profileId: string,
  connectionType: ConnectionTypeEnum,
  formik: FormikProps<FormValues>,
  reportingCurrency: CurrencyCodeEnum,
  strategyAssetCategoryId?: string,
  assetId?: string
): UpdateStrategyAssetInput[] {
  return prepareManualInput(
    'EDIT',
    profileId,
    connectionType,
    formik,
    reportingCurrency,
    strategyAssetCategoryId,
    assetId
  ) as UpdateStrategyAssetInput[];
}
